import styled from '@emotion/styled';
import {
  Button as _Button,
  Card,
  CardActions as _CardActions,
  CardContent as _CardContent,
  Chip,
  IconButton,
  Popover,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { findLast, last } from 'lodash';
import _QRCode from 'qrcode.react';
import { createElement, FC, MouseEvent, MouseEventHandler, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { CreationForShowFragment } from '../../../../generated/graphql';
import { AssignmentState } from '../../../../generated/graphql_offer';
import { getLatestDeliverable, getModificationRequestsWithoutDraft } from '../../../../graphql/creation';
import { toJSDate } from '../../../../graphql/datetime';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import ModificationRequestStateIcon from '../../compound/ModificationRequestStateIcon';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import ImageScreen from '../../primitive/ImageScreen';
import ModificationRequestIssueIcon from '../../primitive/ModificationRequestIssueIcon';
import PublicId from '../../primitive/PublicId';
import QRCodeIcon from '../../primitive/QRCodeIcon';
import PreviewVideoScreen from '../PreviewVideoScreen';

export type Props = {
  assignmentState: AssignmentState;
  creation: CreationForShowFragment;
  requestLatestDeliverableCreativeUrl: () => string | null | undefined;
  onClickSendButton: MouseEventHandler<HTMLElement>;
  className?: string;
  organizationId: string;
  demo?: boolean;
};

const Item: FC<Props> = ({
  assignmentState,
  creation,
  requestLatestDeliverableCreativeUrl,
  onClickSendButton,
  className,
  organizationId,
  demo,
}) => {
  const latestDeliverable = getLatestDeliverable(creation)!;
  const modificationRequestsWithoutDraft = getModificationRequestsWithoutDraft(creation);
  const latestModificationRequest = findLast(
    modificationRequestsWithoutDraft,
    (ModificationRequest) => ModificationRequest.targetDeliverableId === latestDeliverable.id,
  );

  const deliverablesCount = creation.deliverables.length;

  const unresolvedIssueCount = latestModificationRequest
    ? latestModificationRequest.items.filter((item) => item.issue && !item.issue.resolved).length
    : 0;

  const latestIssueComment = latestModificationRequest
    ? last(
        latestModificationRequest.items
          .flatMap((item) => (item.issue ? item.issue.comments : []))
          .sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf()),
      )
    : undefined;

  const modificationRequestState = ():
    | 'given_feedback'
    | 'draft_exist'
    | 'accepting_deliverable'
    | 'in_review'
    | null => {
    // 修正依頼期限が切れている場合は未送信の修正依頼が存在するアイコンを表示しない
    const givenFeedback = latestModificationRequest && latestModificationRequest.state === 'given_feedback';
    const hasDraftModificationRequest =
      !creation.modificationRequestExpired &&
      creation.modificationRequests.filter((request) => request.state === 'draft').length > 0;
    const acceptingDeliverables =
      creation.state === 'modification_requested' &&
      latestModificationRequest &&
      latestModificationRequest.state !== 'given_feedback';
    const reviewingDeliverables =
      acceptingDeliverables && latestModificationRequest ? latestModificationRequest.deliverables.length > 0 : false;
    return givenFeedback
      ? 'given_feedback'
      : reviewingDeliverables
      ? 'in_review'
      : acceptingDeliverables
      ? 'accepting_deliverable'
      : hasDraftModificationRequest
      ? 'draft_exist'
      : null;
  };

  const { network, placement, attribute } = creation.creativeFormat;
  const networkAndPlacementMessage = `${network.name} ${placement.name}`;
  const playbackTimeMessage = attribute.playbackTime ? (
    <FormattedMessage id="creative_format.playback_time" values={{ seconds: attribute.playbackTime.seconds }} />
  ) : (
    ''
  );
  const aspectRatioMessage = attribute.aspectRatio
    ? attribute.aspectRatio.aspectRatio.width === 0 &&
      attribute.aspectRatio.aspectRatio.height === 0 &&
      attribute.aspectRatio.size.width === 0 &&
      attribute.aspectRatio.size.height === 0
      ? ''
      : `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height}`
    : '';

  const sendButton = (
    <CardActionBottun
      size="small"
      variant="text"
      onClick={(e) => {
        if (demo) {
          return;
        }
        e.stopPropagation();
        onClickSendButton(e);
      }}
      disabled={demo}
    >
      <FormattedMessage id="creations_list.item.button_upload" />
    </CardActionBottun>
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const latestDeliverableCreativeUrl = requestLatestDeliverableCreativeUrl();

  return (
    <Card className={className}>
      <CardHeader>
        <HeaderIcons>
          <Chip
            label={<FormattedMessage id="creations_list.item.version_number" values={{ deliverablesCount }} />}
            size="small"
            variant="outlined"
          />
          <ModificationRequestIcons>
            {creation.finalized || assignmentState == 'COMPLETED' ? (
              <CheckCircleIcon fontSize="small" />
            ) : modificationRequestState() ? (
              <ModificationRequestStateIcon state={modificationRequestState()} />
            ) : null}
            {assignmentState == 'ASSIGNED' && latestModificationRequest && unresolvedIssueCount > 0 ? (
              <ModificationRequestIssueState>
                <ModificationRequestIssueIconWithBadge>
                  <ModificationRequestIssueIcon
                    unresolvedIssueCount={unresolvedIssueCount}
                    latestIssueComment={latestIssueComment}
                  />
                </ModificationRequestIssueIconWithBadge>
              </ModificationRequestIssueState>
            ) : null}
          </ModificationRequestIcons>
        </HeaderIcons>
        <CreationTitle>
          <Name variant="subtitle1">
            <CreativeFormat color="textSecondary" noWrap>
              {networkAndPlacementMessage} {aspectRatioMessage} {playbackTimeMessage}
            </CreativeFormat>
            <CreationId>
              <PublicId id={creation.id} color="textSecondary" noWrap />
            </CreationId>
          </Name>
        </CreationTitle>
      </CardHeader>
      <div>
        {creation.creativeFormat.creativeType.key === 'video' && (
          <Thumbnail aspectRatio={[4, 3]}>
            <PreviewVideoScreen src={latestDeliverableCreativeUrl} controls />
          </Thumbnail>
        )}
        {creation.creativeFormat.creativeType.key === 'static_image' && (
          <Thumbnail aspectRatio={[4, 3]}>
            <ImageScreen src={latestDeliverableCreativeUrl} />
          </Thumbnail>
        )}
      </div>
      <CardFooter>
        <CardContent>
          {creation.latestDeliverableSubmittedAt != null && (
            <CreatedAt variant="caption" noWrap>
              <FormattedDate
                value={toJSDate(creation.latestDeliverableSubmittedAt)}
                year="numeric"
                month="2-digit"
                day="2-digit"
                hour="2-digit"
                minute="2-digit"
              >
                {(date: string) => <FormattedMessage id="creations_list.item.created_at" values={{ date }} />}
              </FormattedDate>
            </CreatedAt>
          )}
        </CardContent>
        <CardActions>
          <DetailLink
            color="default"
            variant="text"
            href={`/creations/${creation.id}?org=${organizationId}`}
            target="_blank"
          >
            <FormattedMessage id="creations_list.item.button_detail" />
          </DetailLink>
          {sendButton}
        </CardActions>
        <IconButton onClick={handleClick}>
          <QRCodeIcon size={24} />
        </IconButton>
        {latestDeliverableCreativeUrl ? (
          <Popover
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            onClose={handleClose}
          >
            <QRCode
              value={
                latestDeliverableCreativeUrl.slice(0, 2) === '//'
                  ? `${window.location.protocol}${latestDeliverableCreativeUrl}`
                  : latestDeliverableCreativeUrl
              }
              size={108}
              level="L"
              renderAs="svg"
            />
          </Popover>
        ) : null}
      </CardFooter>
    </Card>
  );
};

export const CardHeader = styled('div')({
  paddingTop: SPACING_REGULAR,
  paddingRight: SPACING_UNIT * 3,
  paddingBottom: SPACING_REGULAR,
  paddingLeft: SPACING_UNIT * 3,
  [mq('sp')]: {
    paddingRight: SPACING_UNIT * 2,
    paddingLeft: SPACING_UNIT * 2,
  },
});

const HeaderIcons = styled('div')({
  paddingBottom: SPACING_REGULAR / 2,
  ...flexibleLayoutContainer({
    direction: 'row',
    justifyContent: 'space-between',
  }),
});

const ModificationRequestIcons = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'row',
    justifyContent: 'flex-end',
    //alignItems: 'center',
  }),
});

const ModificationRequestIssueState = styled('div')({
  ...gridLayoutContainer({
    columns: ['auto'],
    rows: [`calc(100% - 16px)`, '16px'],
    areas: [['icon'], ['issue-comment']],
    justifyContent: 'center',
  }),
  height: 24,
  //textAlign: 'center',
});

const ModificationRequestIssueIconWithBadge = styled('div')({
  ...gridLayoutItem('icon', {
    justifySelf: 'center',
  }),
});

const CreationTitle = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'row',
    justifyContent: 'space-between',
  }),
});

const Thumbnail = styled(AspectRatioFixed)({
  zIndex: 1,
});

const Name = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    fontSize: 13,
  },
});

const CreativeFormat = styled(Typography)({
  [mq('sp')]: {
    fontSize: 12,
    '& div': {
      fontSize: 12,
    },
  },
});

const CreationId = styled('div')({
  [mq('sp')]: {
    fontSize: 12,
    '& div': {
      fontSize: 12,
    },
  },
});

const CardFooter = styled('div')({
  ...gridLayoutContainer({
    justifyContent: 'space-between',
    columns: [`calc(100% - 72px)`, '72px'],
    rows: ['auto', 'auto'],
    areas: [
      ['content', 'qr-code'],
      ['action', 'qr-code'],
    ],
  }),
  [mq('sp')]: {
    ...gridLayoutContainer({
      columns: ['100%'],
      rows: ['auto', 'auto'],
      areas: [['content'], ['action']],
    }),
  },
});

const CardContent = styled(_CardContent)({
  ...gridLayoutItem('content'),
  padding: SPACING_UNIT,
  [mq('sp')]: {
    paddingRight: SPACING_UNIT,
    paddingLeft: SPACING_UNIT,
  },
});

const CardActions = styled(_CardActions)({
  ...gridLayoutItem('action'),
  display: 'flex',
  padding: SPACING_UNIT,
  [mq('sp')]: {
    display: 'block',
    paddingLeft: 4,
  },
});

const CardActionBottun = styled(_Button)({
  whiteSpace: 'nowrap',
});

const QRCode = styled(_QRCode)({
  ...gridLayoutItem('qr-code'),
  padding: `${SPACING_UNIT}px`,
  [mq('sp')]: {
    display: 'none',
  },
});

const DetailLink = (styled(_Button)({
  padding: SPACING_UNIT,
  textTransform: 'none',
}) as unknown) as typeof _Button;

const CreatedAt = styled(Typography)({
  padding: `0 ${SPACING_UNIT}px`,
  [mq('sp')]: {
    fontSize: 11,
    whiteSpace: 'initial',
  },
});

export default Item;
